import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import { useFeatures } from 'FeatureProvider';
import { getValidateItemsAvailabilityFlags } from 'utils/featureFlags/menus';
import {
  getTopMarginClasses,
  getHeightClasses,
  getStickyTopClasses,
  getTopNavWidthClasses,
  getWorkspaceWidthClasses,
  calculateTopOffset,
} from '@subscriptions/presentation/constants';

/**
 * Hook to calculate layout dimensions for Subscriptions domain
 *
 * Encapsulates all the logic for determining top bar height, banner visibility,
 * and generating appropriate Tailwind CSS classes for responsive layout.
 *
 * This hook centralizes the banner visibility logic (enableValidateItemsAvailability
 * && isPublishMenuError && !modeDraft) so it doesn't need to be repeated everywhere.
 *
 * @returns {Object} Layout configuration object
 * @returns {boolean} returns.showBanner - Whether the banner is currently visible
 * @returns {string} returns.topMarginClasses - Tailwind classes for margin-top
 * @returns {string} returns.heightClasses - Tailwind classes for height calculation
 * @returns {string} returns.stickyTopClasses - Tailwind classes for sticky positioning
 * @returns {string} returns.topNavWidthClasses - Tailwind classes for top nav width
 * @returns {string} returns.workspaceWidthClasses - Tailwind classes for workspace width
 * @returns {Object} returns.topOffset - Pixel values for top offset
 * @returns {number} returns.topOffset.mobile - Mobile top offset in pixels
 * @returns {number} returns.topOffset.desktop - Desktop top offset in pixels
 *
 * @example
 * const { showBanner, stickyTopClasses, topOffset } = useSubscriptionsLayoutTopBar();
 *
 * // Use in component
 * <div className={`sticky ${stickyTopClasses}`}>Header</div>
 */
export const useSubscriptionsLayoutTopBar = () => {
  const { openMdDrawer, isPublishMenuError, modeDraft } = useSidebarLayout();
  const { flags } = useFeatures();
  const { enableValidateItemsAvailability } = getValidateItemsAvailabilityFlags(flags);

  /**
   * Banner visibility logic centralized here
   * Banner is shown when:
   * - Feature flag is enabled
   * - There's a publish menu error
   * - Not in draft mode
   */
  const showBanner = enableValidateItemsAvailability && isPublishMenuError && !modeDraft;

  // Calculate all layout classes
  const topMarginClasses = getTopMarginClasses(showBanner);
  const heightClasses = getHeightClasses(showBanner);
  const stickyTopClasses = getStickyTopClasses(showBanner);
  const topNavWidthClasses = getTopNavWidthClasses(openMdDrawer);
  const workspaceWidthClasses = getWorkspaceWidthClasses(openMdDrawer);
  const topOffset = calculateTopOffset(showBanner);

  return {
    showBanner,
    topMarginClasses,
    heightClasses,
    stickyTopClasses,
    topNavWidthClasses,
    workspaceWidthClasses,
    topOffset,
  };
};
